import React, { useState } from 'react';
import { addDoc, collection, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  XMarkIcon,
  StarIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';

const QuickTaskRatingModal = ({ 
  isOpen, 
  onClose, 
  task,
  project,
  onRatingSubmitted 
}) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [smoothCompletion, setSmoothCompletion] = useState(true);
  const [reviewLater, setReviewLater] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If review later is selected, just close
    if (reviewLater) {
      // Mark task for detailed review later
      if (task) {
        try {
          await updateDoc(doc(db, 'projects', project.id, 'tasks', task.id), {
            pendingDetailedReview: true,
            updatedAt: new Date()
          });
        } catch (error) {
          console.error('Error marking task for later review:', error);
        }
      }
      onClose();
      return;
    }

    // Require rating if not reviewing later
    if (rating === 0) {
      alert('Please provide a rating or choose to review later');
      return;
    }

    setSubmitting(true);
    try {
      // Create quick rating document
      const quickRatingData = {
        taskId: task.id,
        taskName: task.name,
        taskCategory: task.category || 'general',
        projectId: project.id,
        projectName: project.name,
        supplierId: task.assignedSupplierId || null,
        supplierName: task.assignedSupplierName || 'Unknown Supplier',
        rating: rating,
        comment: comment.trim(),
        smoothCompletion: smoothCompletion,
        isQuickRating: true,
        createdAt: serverTimestamp(),
        createdBy: 'admin' // Could be dynamic based on user
      };

      await addDoc(collection(db, 'taskRatings'), quickRatingData);

      // Update task with quick rating
      await updateDoc(doc(db, 'projects', project.id, 'tasks', task.id), {
        quickRating: rating,
        quickRatingComment: comment.trim(),
        quickRatingDate: new Date(),
        hasQuickRating: true,
        pendingDetailedReview: false,
        updatedAt: new Date()
      });

      // If there's a supplier, update their recent rating
      if (task.assignedSupplierId) {
        try {
          await updateDoc(doc(db, 'suppliers', task.assignedSupplierId), {
            lastQuickRating: rating,
            lastQuickRatingDate: new Date(),
            updatedAt: new Date()
          });
        } catch (error) {
          console.error('Error updating supplier quick rating:', error);
        }
      }

      // Success feedback
      const successMessage = smoothCompletion 
        ? `✅ Task rated ${rating} stars!`
        : `✅ Task rated ${rating} stars. We'll follow up on the issues noted.`;
      
      alert(successMessage);
      
      if (onRatingSubmitted) {
        onRatingSubmitted(quickRatingData);
      }
      onClose();
      
    } catch (error) {
      console.error('Error submitting quick rating:', error);
      alert('Failed to submit rating: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <button
          key={i}
          type="button"
          onClick={() => setRating(i)}
          className="focus:outline-none transition-transform hover:scale-110"
        >
          {i <= rating ? (
            <StarSolid className="w-10 h-10 text-yellow-400" />
          ) : (
            <StarIcon className="w-10 h-10 text-gray-300 hover:text-yellow-300" />
          )}
        </button>
      );
    }
    return <div className="flex justify-center space-x-2">{stars}</div>;
  };

  if (!isOpen || !task) return null;

  // Determine supplier info
  const supplierName = task.assignedSupplierName || task.directQuoteSupplier || 'the supplier';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <CheckCircleIcon className="w-8 h-8 text-green-600 mr-3" />
              <div>
                <h2 className="text-xl font-bold text-gray-900">Quick Task Rating</h2>
                <p className="text-sm text-gray-600">Task completed!</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={submitting}
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Task Context */}
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="font-medium text-gray-900">{task.name}</p>
              <p className="text-sm text-gray-600">by {supplierName}</p>
            </div>

            {/* Quick Rating */}
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700 mb-3">
                How did {supplierName} perform?
              </p>
              {renderStars()}
              {rating > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {rating === 5 && 'Excellent!'}
                  {rating === 4 && 'Good'}
                  {rating === 3 && 'Satisfactory'}
                  {rating === 2 && 'Below Expectations'}
                  {rating === 1 && 'Poor'}
                </p>
              )}
            </div>

            {/* Smooth Completion Checkbox */}
            <div className="flex items-center space-x-3 px-2">
              <input
                type="checkbox"
                id="smooth"
                checked={smoothCompletion}
                onChange={(e) => setSmoothCompletion(e.target.checked)}
                className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
              />
              <label htmlFor="smooth" className="text-sm text-gray-700">
                Everything went smoothly
              </label>
            </div>

            {/* Optional Comment (shown if not smooth or rating <= 3) */}
            {(!smoothCompletion || rating <= 3) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {rating <= 3 ? 'What went wrong?' : 'Any issues to note?'}
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                  placeholder="Brief note about issues..."
                />
              </div>
            )}

            {/* Optional positive comment for high ratings */}
            {rating >= 4 && smoothCompletion && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Any highlights? (optional)
                </label>
                <input
                  type="text"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                  placeholder="e.g., Great communication, ahead of schedule..."
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-2 pt-2">
              <button
                type="submit"
                disabled={submitting || (rating === 0 && !reviewLater)}
                className={`w-full flex items-center justify-center px-4 py-2.5 rounded-lg font-medium transition-colors ${
                  !submitting && rating > 0
                    ? 'bg-pink-600 text-white hover:bg-pink-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <StarIcon className="w-4 h-4 mr-2" />
                    Submit Rating
                  </>
                )}
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setReviewLater(true);
                  handleSubmit(new Event('submit'));
                }}
                className="w-full flex items-center justify-center px-4 py-2 text-gray-600 hover:text-gray-800 text-sm font-medium"
                disabled={submitting}
              >
                <ClockIcon className="w-4 h-4 mr-2" />
                Review in Detail Later
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default QuickTaskRatingModal;